import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  Switch,
  Table,
  Space,
  Tag,
  Typography,
  Input,
  Tooltip,
  Checkbox,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  SyncOutlined,
  RetweetOutlined,
  SettingOutlined,
  CompassOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRouteAssignments,
  saveRouteAssignment,
  deleteRouteAssignment,
  fetchWorkRoutes,
} from "../workRoutesThunk";

const dayOptions = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const { Text } = Typography;

// Plain-English hint + icon for each schedule type, shown on hover in the
// dropdown (see the Select's optionRender below) so an admin who isn't
// familiar with the scheduling jargon can pick the right one without
// leaving the form.
const scheduleTypeOptions = [
  {
    label: "One Time",
    value: "once",
    icon: <CalendarOutlined />,
    hint: "For a route that only needs to be done once. Pick the exact date in Start Date -- that's the only day it will run.",
  },
  {
    label: "Daily",
    value: "daily",
    icon: <SyncOutlined />,
    hint: "For a route the staff must do every single day. No days to pick -- it just repeats daily until the end date (or forever, if you leave End Date blank).",
  },
  // {
  //   label: "Weekdays",
  //   value: "weekdays",
  // },
  {
    label: "Weekly",
    value: "weekly",
    icon: <CalendarOutlined />,
    hint: "For a route that repeats every week on the same day(s), e.g. every Monday and Thursday. Tick the days under Visit Days.",
  },
  {
    label: "Biweekly",
    value: "biweekly",
    icon: <RetweetOutlined />,
    hint: "For a route that happens every other week (not every week) on the day(s) you tick. Choose Week A or Week B to set which week it starts on.",
  },
  {
    label: "Custom",
    value: "custom",
    icon: <SettingOutlined />,
    hint: "For an unusual repeat pattern that Weekly or Biweekly can't do, e.g. once every 3 or 4 weeks. Set how many weeks the cycle is and which week in that cycle this route runs.",
  },
  // {
  //   label: "Monthly",
  //   value: "monthly",
  // },
  {
    label: "Flexible (No Fixed Schedule)",
    value: "flexible",
    icon: <CompassOutlined />,
    hint: "No fixed days at all. Use this to hand a staff member a list of many points (e.g. shops) to visit whenever they're free, in any order, over as many days as it takes. Best paired with an Unordered route.",
  },
];

const normalizeScheduleDays = (days) => {
  if (!Array.isArray(days)) {
    return [];
  }

  return days
    .map((day) => {
      if (typeof day === "number") {
        return day;
      }

      const numericDay = Number(day);

      if (!Number.isNaN(numericDay)) {
        return numericDay;
      }

      const dayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      return dayMap[String(day).toLowerCase()];
    })
    .filter((day) => day !== undefined && day !== null);
};

const getDayName = (day) => {
  const found = dayOptions.find(
    (option) => Number(option.value) === Number(day),
  );

  return found?.label || day;
};

const Assignments = ({ employees = [] }) => {
  const dispatch = useDispatch();

  const { workRoutes, routeAssignments } = useSelector(
    (state) => state.workRoutes,
  );

  const [open, setOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [search, setSearch] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [rotationCycle, setRotationCycle] = useState(2);
  const [rotationWeek, setRotationWeek] = useState(1);

  const [form] = Form.useForm();

  const selectedEmployeeId = Form.useWatch("employee_id", form);

  const selectedScheduleType = Form.useWatch("schedule_type", form);

  const selectedScheduleWeek = Form.useWatch("schedule_week", form);

  const selectedRotationCycle = Form.useWatch("rotation_cycle", form);

  const selectedRotationWeek = Form.useWatch("rotation_week", form);

  useEffect(() => {
    dispatch(fetchRouteAssignments());
    dispatch(fetchWorkRoutes());
  }, [dispatch]);

  /*
   * Employee options
   */
  const employeeOptions = useMemo(() => {
    return employees.map((emp) => ({
      label: emp.employee_name || emp.name || emp.user_name,
      value: emp.employee_id,
    }));
  }, [employees]);

  /*
   * Replacement employee options
   */
  const replacementEmployeeOptions = useMemo(() => {
    return employees
      .filter((emp) => emp.employee_id !== selectedEmployeeId)
      .map((emp) => ({
        label: emp.employee_name || emp.name || emp.user_name,
        value: emp.employee_id,
      }));
  }, [employees, selectedEmployeeId]);

  /*
   * Prevent selecting the same employee as replacement.
   */
  useEffect(() => {
    const replacementEmployee = form.getFieldValue("replacement_employee_id");

    if (replacementEmployee === selectedEmployeeId) {
      form.setFieldValue("replacement_employee_id", undefined);
    }
  }, [selectedEmployeeId, form]);

  /*
   * Populate form when editing.
   */
  useEffect(() => {
    if (!open || !selectedAssignment) {
      return;
    }

    setRotationCycle(Number(selectedAssignment.rotation_cycle) || 2);

    setRotationWeek(Number(selectedAssignment.rotation_week) || 1);

    form.setFieldsValue({
      employee_id: selectedAssignment.employee_id,

      route_id: selectedAssignment.route_id,

      assignment_date: selectedAssignment.assignment_date
        ? dayjs(selectedAssignment.assignment_date)
        : null,

      schedule_end_date: selectedAssignment.schedule_end_date
        ? dayjs(selectedAssignment.schedule_end_date)
        : null,

      schedule_days: normalizeScheduleDays(selectedAssignment.schedule_days),

      schedule_type: selectedAssignment.schedule_type || "once",

      schedule_week: selectedAssignment.schedule_week || "A",

      rotation_cycle: Number(selectedAssignment.rotation_cycle) || null,

      rotation_week: Number(selectedAssignment.rotation_week) || null,

      replacement_employee_id: selectedAssignment.replacement_employee_id,

      is_temporary: !!selectedAssignment.is_temporary,

      status: selectedAssignment.status || "assigned",
    });
  }, [open, selectedAssignment, form]);

  /*
   * Add assignment.
   */
  const openAddModal = () => {
    setSelectedAssignment(null);

    form.resetFields();

    form.setFieldsValue({
      schedule_type: "once",
      schedule_week: "A",
      schedule_days: [],
      rotation_cycle: 2,
      rotation_week: 1,
      is_temporary: false,
      status: "assigned",
    });

    setOpen(true);
  };

  /*
   * Edit assignment.
   */
  const openEditModal = (record) => {
    setSelectedAssignment(record);
    setOpen(true);
  };

  /*
   * Schedule type change.
   */
  const handleScheduleTypeChange = (value) => {
    if (value === "once") {
      form.setFieldsValue({
        schedule_days: [],
        schedule_week: null,
        rotation_cycle: null,
        rotation_week: null,
        schedule_end_date: null,
      });

      return;
    }

    if (value === "biweekly") {
      form.setFieldsValue({
        schedule_days: form.getFieldValue("schedule_days") || [],
        schedule_week: form.getFieldValue("schedule_week") || "A",
        rotation_cycle: null,
        rotation_week: null,
      });

      return;
    }

    if (value === "custom") {
      form.setFieldsValue({
        schedule_days: form.getFieldValue("schedule_days") || [],
        schedule_week: null,
        rotation_cycle: form.getFieldValue("rotation_cycle") || 2,
        rotation_week: form.getFieldValue("rotation_week") || 1,
      });

      return;
    }

    form.setFieldsValue({
      schedule_week: null,
      rotation_cycle: null,
      rotation_week: null,
    });

    /*
     * Daily / Weekdays / Monthly / Flexible don't use
     * manually selected days.
     */
    if (["daily", "weekdays", "monthly", "flexible"].includes(value)) {
      form.setFieldValue("schedule_days", []);
    }
  };

  const handleRotationCycleChange = (value) => {
    const cycle = Number(value);

    setRotationCycle(cycle);

    if (rotationWeek > cycle) {
      setRotationWeek(1);
    }
  };

  /*
   * Save assignment.
   */

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const scheduleType = values.schedule_type || "once";

      let scheduleDays = [];

      if (["weekly", "custom", "biweekly"].includes(scheduleType)) {
        scheduleDays = normalizeScheduleDays(values.schedule_days);
      }

      const cleanedValues = {
        ...values,

        schedule_days: scheduleDays,

        schedule_week:
          scheduleType === "biweekly"
            ? values.schedule_week === "B"
              ? "B"
              : "A"
            : null,

        rotation_cycle:
          scheduleType === "custom" ? Number(values.rotation_cycle) : null,

        rotation_week:
          scheduleType === "custom" ? Number(values.rotation_week) : null,

        assignment_date: values.assignment_date.format("YYYY-MM-DD"),

        schedule_end_date: values.schedule_end_date
          ? values.schedule_end_date.format("YYYY-MM-DD")
          : null,
      };

      await dispatch(
        saveRouteAssignment({
          id: selectedAssignment?.id,
          ...cleanedValues,
        }),
      ).unwrap();

      setOpen(false);
      form.resetFields();
      setSelectedAssignment(null);
    } catch (errorInfo) {
      console.error("Save route assignment error:", errorInfo);
    }
  };

  /*
   * Search.
   */
  const filteredAssignments = routeAssignments.filter((item) => {
    const employeeName = item.employee_name?.toLowerCase() || "";

    const routeName = item.route_name?.toLowerCase() || "";

    const searchValue = search.toLowerCase();

    return (
      employeeName.includes(searchValue) || routeName.includes(searchValue)
    );
  });

  const statusColorMap = {
    assigned: "blue",
    in_progress: "orange",
    completed: "green",
    cancelled: "red",
  };

  /*
   * Assignment table.
   */
  const columns = [
    {
      title: "#",
      key: "serialNumber",
      width: 65,
      align: "center",

      render: (_, __, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },

    {
      title: "Employee",

      render: (_, record) => <Text>{record.employee_name || record.name}</Text>,
    },

    {
      title: "Route",
      dataIndex: "route_name",
    },

    {
      title: "Schedule Type",

      render: (_, record) => {
        const scheduleType = record.schedule_type || "once";

        const label =
          scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);

        const showScheduleDays = ["weekly", "custom", "biweekly"].includes(
          scheduleType,
        );

        const normalizedDays = normalizeScheduleDays(record.schedule_days);

        const days =
          showScheduleDays && normalizedDays.length > 0
            ? normalizedDays.map(getDayName).join(", ")
            : null;

        const tag = (
          <Tag color="blue">
            {label}

            {scheduleType === "biweekly" && record.schedule_week && (
              <> - Week {record.schedule_week}</>
            )}

            {scheduleType === "custom" &&
              record.rotation_cycle &&
              record.rotation_week && (
                <div style={{ marginTop: 6 }}>
                  <strong>Rotation:</strong> Week {record.rotation_week} of{" "}
                  {record.rotation_cycle}
                </div>
              )}

            {showScheduleDays && days && (
              <InfoCircleOutlined
                style={{
                  marginLeft: 4,
                  color: "#0e3e8b",
                  cursor: "pointer",
                }}
              />
            )}
          </Tag>
        );

        if (!showScheduleDays || !days) {
          return tag;
        }

        return (
          <Tooltip
            placement="bottomLeft"
            color="#ffffff"
            trigger={["hover", "click"]}
            title={
              <div
                style={{
                  color: "#000",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Assigned Days
                </div>

                <div>{days}</div>

                {scheduleType === "biweekly" && record.schedule_week && (
                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    <strong>Rotation:</strong> Week {record.schedule_week}
                  </div>
                )}
              </div>
            }
          >
            {tag}
          </Tooltip>
        );
      },
    },

    {
      title: "Schedule",

      render: (_, record) => {
        if (record.schedule_type === "once") {
          return dayjs(record.assignment_date).format("DD-MM-YYYY");
        }

        return (
          <>
            {dayjs(record.assignment_date).format("DD-MM-YYYY")}

            {" → "}

            {record.schedule_end_date
              ? dayjs(record.schedule_end_date).format("DD-MM-YYYY")
              : record.schedule_type === "flexible"
                ? "Ongoing"
                : "-"}
          </>
        );
      },
    },

    {
      title: "Replacement",

      render: (_, record) => {
        if (!record.replacement_employee_name) {
          return "-";
        }

        return <Text>{record.replacement_employee_name}</Text>;
      },
    },

    {
      title: "Temporary",

      dataIndex: "is_temporary",

      render: (v) =>
        v ? (
          <Tag color="orange">Temporary</Tag>
        ) : (
          <Tag color="green">Regular</Tag>
        ),
    },

    {
      title: "Status",

      dataIndex: "status",

      render: (v) => (
        <Tag color={statusColorMap[v] || "default"}>
          {v ? v.charAt(0).toUpperCase() + v.slice(1) : "-"}
        </Tag>
      ),
    },

    {
      title: "Action",

      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              dispatch(
                deleteRouteAssignment({
                  id: record.id,
                }),
              )
            }
          />
        </Space>
      ),
    },
  ];

  const isDaySelectionRequired = ["weekly", "custom", "biweekly"].includes(
    selectedScheduleType,
  );

  const isBiweekly = selectedScheduleType === "biweekly";

  return (
    <div>
      <Card title="Route Assignments">
        <Row
          gutter={[16, 16]}
          style={{
            marginBottom: 16,
          }}
          justify="space-between"
          align="middle"
        >
          <Col xs={24} sm={14} md={10} lg={8}>
            <Input
              placeholder="Search employee or route"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
              }}
            />
          </Col>

          <Col
            xs={24}
            sm={10}
            md={6}
            style={{
              textAlign: "right",
            }}
          >
            <Button type="primary" onClick={openAddModal} block>
              Assign Route
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredAssignments}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "50", "100"],

            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,

            onChange: (page, pageSize) => {
              setPagination({
                current: page,
                pageSize,
              });
            },

            onShowSizeChange: (current, size) => {
              setPagination({
                current: 1,
                pageSize: size,
              });
            },
          }}
          scroll={{
            x: "max-content",
          }}
        />
      </Card>

      <Modal
        open={open}
        title={selectedAssignment ? "Edit Assignment" : "Assign Route"}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
          setSelectedAssignment(null);
        }}
        destroyOnClose
        onOk={handleSave}
        width={700}
        style={{
          top: 20,
        }}
      >
        <Form
          layout="vertical"
          form={form}
          key={selectedAssignment?.id || "new"}
        >
          <Row gutter={[16, 0]}>
            {/* Employee */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Employee"
                name="employee_id"
                rules={[
                  {
                    required: true,
                    message: "Please select an employee",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={employeeOptions}
                />
              </Form.Item>
            </Col>

            {/* Route */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Route"
                name="route_id"
                rules={[
                  {
                    required: true,
                    message: "Please select a route",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={workRoutes.map((route) => ({
                    label: route.name,
                    value: route.id,
                  }))}
                />
              </Form.Item>
            </Col>

            {/* Start Date */}
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label="Start Date"
                name="assignment_date"
                rules={[
                  {
                    required: true,
                    message: "Please select a start date",
                  },
                ]}
              >
                <DatePicker
                  style={{
                    width: "100%",
                  }}
                />
              </Form.Item>
            </Col>

            {/* Schedule Type */}
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Schedule Type
                    <Tooltip title="Controls when this route is assigned to the employee. Open the dropdown and hover the (i) next to each option for what it means and how to set it up.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="schedule_type"
                rules={[
                  {
                    required: true,
                    message: "Please select a schedule type",
                  },
                ]}
              >
                <Select
                  onChange={handleScheduleTypeChange}
                  options={scheduleTypeOptions}
                  listHeight={320}
                  optionRender={(option) => (
                    <Space
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Space size={8}>
                        <span style={{ color: "#595959" }}>
                          {option.data.icon}
                        </span>
                        {option.data.label}
                      </Space>

                      <Tooltip
                        title={option.data.hint}
                        placement="right"
                        overlayStyle={{ maxWidth: 320 }}
                      >
                        <InfoCircleOutlined
                          style={{ color: "#0e3e8b", cursor: "help" }}
                        />
                      </Tooltip>
                    </Space>
                  )}
                />
              </Form.Item>
            </Col>

            {/* Schedule Days */}
            {isDaySelectionRequired && (
              <Col span={24}>
                <Form.Item
                  label={
                    <Space>
                      Visit Days
                      <Tooltip title="Select the days on which this route should be assigned. For Biweekly, these days repeat according to the selected rotation week.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="schedule_days"
                  rules={[
                    {
                      required: true,
                      type: "array",
                      min: 1,
                      message: "Please select at least one day",
                    },
                  ]}
                >
                  <Checkbox.Group options={dayOptions} />
                </Form.Item>
              </Col>
            )}

            {/* Biweekly Rotation */}
            {isBiweekly && (
              <Col span={24}>
                <Form.Item
                  label={
                    <Space>
                      Rotation Week
                      <Tooltip title="Week A and Week B alternate every week starting from the assignment start date.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="schedule_week"
                  rules={[
                    {
                      required: true,
                      message: "Please select a rotation week",
                    },
                  ]}
                >
                  <Select
                    options={[
                      {
                        label: "Week A",
                        value: "A",
                      },
                      {
                        label: "Week B",
                        value: "B",
                      },
                    ]}
                  />
                </Form.Item>

                <div
                  style={{
                    marginTop: -8,
                    marginBottom: 16,
                    padding: "10px 12px",
                    background: "#f5f5f5",
                    borderRadius: 6,
                  }}
                >
                  <Text type="secondary">
                    {selectedScheduleWeek === "B"
                      ? "This assignment will run during Week B of the biweekly rotation."
                      : "This assignment will run during Week A of the biweekly rotation."}
                  </Text>
                </div>
              </Col>
            )}

            {/* Custom */}
            {selectedScheduleType === "custom" && (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={
                      <Space>
                        Rotation Cycle
                        <Tooltip title="Defines how many weeks are in the rotation cycle. For example, 3 means the pattern repeats every 3 weeks.">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    name="rotation_cycle"
                    rules={[
                      {
                        required: true,
                        message: "Please select a rotation cycle",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select cycle"
                      options={[
                        { label: "2 Weeks", value: 2 },
                        { label: "3 Weeks", value: 3 },
                        { label: "4 Weeks", value: 4 },
                        { label: "5 Weeks", value: 5 },
                        { label: "6 Weeks", value: 6 },
                        { label: "7 Weeks", value: 7 },
                        { label: "8 Weeks", value: 8 },
                        { label: "9 Weeks", value: 9 },
                        { label: "10 Weeks", value: 10 },
                        { label: "11 Weeks", value: 11 },
                        { label: "12 Weeks", value: 12 },
                      ]}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label={
                      <Space>
                        Rotation Week
                        <Tooltip title="Select which week within the rotation cycle this assignment should run.">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    name="rotation_week"
                    rules={[
                      {
                        required: true,
                        message: "Please select a rotation week",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select rotation week"
                      options={Array.from(
                        {
                          length: Number(selectedRotationCycle) || 0,
                        },
                        (_, index) => ({
                          label: `Week ${index + 1}`,
                          value: index + 1,
                        }),
                      )}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <div
                    style={{
                      marginTop: -8,
                      marginBottom: 16,
                      padding: "10px 12px",
                      background: "#f5f5f5",
                      borderRadius: 6,
                    }}
                  >
                    <Text type="secondary">
                      {selectedRotationCycle && selectedRotationWeek
                        ? `This assignment will run on the selected visit days during Week ${selectedRotationWeek} of every ${selectedRotationCycle}-week rotation.`
                        : "Select the rotation cycle and rotation week."}
                    </Text>
                  </div>
                </Col>
              </>
            )}

            {/* Schedule End Date */}
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.schedule_type !== currentValues.schedule_type
                }
              >
                {({ getFieldValue }) => {
                  const currentScheduleType = getFieldValue("schedule_type");
                  const isOnce = currentScheduleType === "once";
                  // Flexible assignments have no fixed cadence, so an end
                  // date isn't required -- leaving it blank means the
                  // assignment just stays active until an admin changes or
                  // cancels it. Unlike "once" it's still settable, in case
                  // the admin wants this particular assignment to expire on
                  // a known date (e.g. a time-boxed campaign).
                  const isFlexible = currentScheduleType === "flexible";

                  return (
                    <Form.Item
                      label={
                        isFlexible ? (
                          <Space>
                            Schedule End Date (Optional)
                            <Tooltip title="Leave blank to keep this assignment active indefinitely -- the employee can check in to any route point, any day, until you end or cancel it.">
                              <InfoCircleOutlined />
                            </Tooltip>
                          </Space>
                        ) : (
                          "Schedule End Date"
                        )
                      }
                      name="schedule_end_date"
                      rules={[
                        {
                          required: !isOnce && !isFlexible,
                          message: "Please select a schedule end date",
                        },
                      ]}
                    >
                      <DatePicker
                        style={{
                          width: "100%",
                        }}
                        disabled={isOnce}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>

            {/* Replacement Employee */}
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Replacement Employee
                    <Tooltip
                      title="Backup employee who can execute this route if the primary employee is unavailable."
                      placement="bottom"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="replacement_employee_id"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={replacementEmployeeOptions}
                />
              </Form.Item>
            </Col>

            {/* Temporary */}
            <Col xs={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Temporary Assignment
                    <Tooltip
                      title="Used for temporary route coverage without affecting permanent route planning."
                      placement="bottom"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="is_temporary"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            {/* Status */}
            {selectedAssignment && (
              <Col xs={12} md={12}>
                <Form.Item
                  label={
                    <Space>
                      Status
                      <Tooltip
                        title="Operational state of this route assignment."
                        placement="bottom"
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="status"
                >
                  <Select
                    options={[
                      {
                        label: "Assigned",
                        value: "assigned",
                      },
                      {
                        label: "In Progress",
                        value: "in_progress",
                      },
                      {
                        label: "Completed",
                        value: "completed",
                      },
                      {
                        label: "Cancelled",
                        value: "cancelled",
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Assignments;
